import { CSVIcon } from '@/components/icons/csv-icon'
import { TableIcon } from '@/components/icons/table-icon'
import { UploadIcon } from '@/components/icons/upload-icon'
import { Button } from '@/components/ui/button'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { toast } from '@/components/ui/toaster'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import { cn } from '@/lib/utils'
import { T } from '@tolgee/react'
import { XIcon } from 'lucide-react'
import { useCallback, useRef } from 'react'
import { Field } from 'react-final-form'

// Constants
const FILE_UPLOAD_LIMITS = {
  MAX_FILE_COUNT: 5,
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
} as const

const FILE_TYPES = {
  CSV: 'text/csv',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
} as const

const ALLOWED_MIME_TYPES = [FILE_TYPES.CSV, FILE_TYPES.XLSX] as const

// Types
interface PromptFormFileUploadProps {
  disabled?: boolean
}

interface FileComponentProps {
  file: File
  className?: string
  handleRemove: () => void
  disabled?: boolean
}

type ValidationError = string | null

// Utility functions
const fileUtils = {
  convertFileListToArray: (fileList: FileList | null): File[] => {
    return fileList ? Array.from(fileList) : []
  },

  convertArrayToFileList: (files: File[]): FileList => {
    const dataTransfer = new DataTransfer()
    files.forEach((file) => dataTransfer.items.add(file))
    return dataTransfer.files
  },

  getFileIcon: (mimeType: string) => {
    switch (mimeType) {
      case FILE_TYPES.CSV:
        return <CSVIcon className='text-neutral-150' />
      case FILE_TYPES.XLSX:
        return <TableIcon className='text-neutral-150' />
      default:
        return null
    }
  },
}

// Validation functions
const validateFileCount = (files: File[]): ValidationError => {
  if (files.length > FILE_UPLOAD_LIMITS.MAX_FILE_COUNT) {
    return `Maximum ${FILE_UPLOAD_LIMITS.MAX_FILE_COUNT} files allowed`
  }
  return null
}

const validateFileTypes = (files: File[]): ValidationError => {
  const invalidFile = files.find((file) => !ALLOWED_MIME_TYPES.includes(file.type as any))
  if (invalidFile) {
    return 'Only CSV and XLSX files are allowed'
  }
  return null
}

const validateFileSizes = (files: File[]): ValidationError => {
  const oversizedFile = files.find((file) => file.size > FILE_UPLOAD_LIMITS.MAX_FILE_SIZE)
  if (oversizedFile) {
    return `File "${oversizedFile.name}" is too large. Maximum size is 10MB`
  }
  return null
}

const validateFiles = (fileList: FileList): ValidationError => {
  const files = Array.from(fileList)

  return validateFileCount(files) || validateFileTypes(files) || validateFileSizes(files)
}

// File component
function FileComponent({ file, handleRemove, className, disabled }: FileComponentProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 rounded-sm border bg-neutral-white px-1',
        className,
      )}
    >
      <div className='flex max-w-[140px] flex-1 items-center gap-1'>
        {fileUtils.getFileIcon(file.type)}
        <span className='truncate text-xs font-light'>{file.name}</span>
      </div>
      <TooltipIconButton
        keyName='home.taskForm.upload.remove'
        contentProps={{ side: 'right', sideOffset: 12 }}
        onClick={handleRemove}
        className='rounded-none hover:bg-transparent focus:bg-transparent active:bg-transparent'
        disabled={disabled}
      >
        <XIcon className='size-4 text-neutral-150' />
      </TooltipIconButton>
    </div>
  )
}

// Single file display component
function SingleFileDisplay({
  file,
  onDelete,
  disabled,
}: {
  file: File
  onDelete: () => void
  disabled?: boolean
}) {
  return <FileComponent file={file} handleRemove={onDelete} disabled={disabled} />
}

// Multiple files display component
function MultipleFilesDisplay({
  files,
  onDelete,
  disabled,
}: {
  files: File[]
  onDelete: (index: number) => void
  disabled?: boolean
}) {
  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Button
          unstyled
          className='border border-neutral-25 bg-neutral-white px-2 py-[1px] text-xs font-light text-neutral-500'
        >
          <T keyName='home.taskForm.upload.files' params={{ length: files.length }} />
        </Button>
      </HoverCardTrigger>
      <HoverCardContent
        align='start'
        side='bottom'
        className='w-[200px] gap-1.5 rounded-lg border border-white bg-neutral-25 p-1.5'
      >
        {files.map((file, index) => (
          <FileComponent
            key={`${file.name}-${index}`}
            file={file}
            handleRemove={() => onDelete(index)}
            className='cursor-pointer rounded-[4px] border-0 bg-transparent px-0 py-0.5 hover:bg-neutral-50'
            disabled={disabled}
          />
        ))}
      </HoverCardContent>
    </HoverCard>
  )
}

// Main component
function PromptFormFileUpload({ disabled }: PromptFormFileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileInputChange = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement>,
      currentFiles: File[],
      onFileChange: (files: FileList) => void,
    ) => {
      const newFiles = event.target.files

      if (!newFiles?.length) return

      const combinedFiles = [...currentFiles, ...Array.from(newFiles)]

      // Validate combined files count first
      const countError = validateFileCount(combinedFiles)
      if (countError) {
        toast.error(countError)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      // Validate all files
      const combinedFileList = fileUtils.convertArrayToFileList(combinedFiles)
      const validationError = validateFiles(combinedFileList)

      if (validationError) {
        toast.error(validationError)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      onFileChange(combinedFileList)

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [],
  )

  const handleUploadButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    fileInputRef.current?.click()
    e.currentTarget.blur()
  }, [])

  const handleFileDelete = useCallback(
    (
      indexToDelete: number,
      currentFiles: File[],
      onFileChange: (files: FileList | null) => void,
    ) => {
      const updatedFiles = currentFiles.filter((_, index) => index !== indexToDelete)

      if (updatedFiles.length === 0) {
        onFileChange(null)
      } else {
        onFileChange(fileUtils.convertArrayToFileList(updatedFiles))
      }
    },
    [],
  )

  const renderFiles = useCallback(
    (files: File[], onDelete: (index: number) => void) => {
      if (files.length === 0) return null

      if (files.length === 1) {
        return (
          <SingleFileDisplay file={files[0]} onDelete={() => onDelete(0)} disabled={disabled} />
        )
      }

      return <MultipleFilesDisplay files={files} onDelete={onDelete} disabled={disabled} />
    },
    [disabled],
  )

  return (
    <Field<FileList | null> name='files'>
      {({ input }) => {
        const currentFiles = fileUtils.convertFileListToArray(input.value)

        const handleRemove = (index: number) => {
          handleFileDelete(index, currentFiles, input.onChange)
        }

        const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          handleFileInputChange(event, currentFiles, input.onChange)
        }

        return (
          <div className='flex items-center py-1'>
            <input
              ref={fileInputRef}
              type='file'
              accept='.csv,.xlsx'
              multiple
              onChange={handleFileChange}
              className='hidden'
            />

            <TooltipIconButton
              keyName='home.taskForm.upload.tooltip'
              className='m-0.5 size-6'
              contentProps={{ align: 'center', side: 'right', sideOffset: 2 }}
              onClick={handleUploadButtonClick}
              disabled={disabled}
              size={'auto'}
            >
              <UploadIcon className='size-6' />
            </TooltipIconButton>

            {renderFiles(currentFiles, handleRemove)}
          </div>
        )
      }}
    </Field>
  )
}

export { PromptFormFileUpload }
